// src/components/roles/RoleFilters/RoleFilters.hooks.tsx
import { useCallback, useMemo } from 'react';
import { RoleFilter } from '../../../types/role.types';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UseRoleFiltersProps {
  filters: RoleFilter;
  setFilters: React.Dispatch<React.SetStateAction<RoleFilter>>;
  appliedFilters: RoleFilter;
  onClearAppliedField: (patch: Partial<RoleFilter>) => void;
  showMoreFilters: boolean;
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
  onFilterChange?: (newFilters: Partial<RoleFilter>) => void;
}

export const useRoleFilters = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
}: UseRoleFiltersProps) => {
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSearch();
      }
    },
    [onSearch],
  );

  const handleDateChange = useCallback(
    (name: 'startDate' | 'endDate', value: string) => {
      setFilters((prev) => ({ ...prev, [name]: value || undefined }));
    },
    [setFilters],
  );

  const renderStatusValue = useCallback((selected: unknown): React.ReactNode => {
    if (!selected || selected === 'all') return <span style={{ color: 'gray' }}>Todos</span>;
    if (selected === 'active') return 'Activo';
    if (selected === 'blocked') return 'Bloqueado';
    return String(selected);
  }, []);

  const renderSortByValue = useCallback((selected: unknown): React.ReactNode => {
    if (!selected) return <span style={{ color: 'gray' }}>Seleccionar</span>;
    if (selected === 'name') return 'Nombre';
    if (selected === 'createdAt') return 'Fecha de creación';
    if (selected === 'updatedAt') return 'Última actualización';
    if (selected === 'userCount') return 'Número de usuarios';
    return String(selected);
  }, []);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.searchTerm) {
      chips.push({
        key: 'searchTerm',
        label: `Búsqueda: ${applied.searchTerm}`,
        onRemove: () => onClearAppliedField({ searchTerm: '' }),
      });
    }
    if (applied.status && applied.status !== 'all') {
      const statusLabel =
        applied.status === 'active'
          ? 'Activo'
          : applied.status === 'blocked'
            ? 'Bloqueado'
            : applied.status;
      chips.push({
        key: 'status',
        label: `Estado: ${statusLabel}`,
        onRemove: () => onClearAppliedField({ status: 'all' }),
      });
    }
    if (applied.startDate) {
      chips.push({
        key: 'startDate',
        label: `Desde: ${applied.startDate}`,
        onRemove: () => onClearAppliedField({ startDate: undefined }),
      });
    }
    if (applied.endDate) {
      chips.push({
        key: 'endDate',
        label: `Hasta: ${applied.endDate}`,
        onRemove: () => onClearAppliedField({ endDate: undefined }),
      });
    }

    return chips;
  }, [appliedFilters, onClearAppliedField]);

  return {
    filters,
    showMoreFilters,
    handleInputChange,
    handleSelectChange,
    handleKeyPress,
    handleDateChange,
    renderStatusValue,
    renderSortByValue,
    onSearch,
    onReset,
    onToggleMoreFilters,
    activeFilterChips,
  };
};
